import React, { useRef, useState } from 'react';
import { Alert, Button, Group, Modal, Progress, Stack, Text } from '@mantine/core';
import { CheckCircle, FileCsv, Warning } from '@phosphor-icons/react';

import { bulkUpdateMasterCsv, type BulkUpdateResult, type MasterEntityType, type MasterTableColumnMeta } from '@api/entity-attributes';

interface BulkUploadModalProps {
    opened: boolean;
    onClose: () => void;
    entityType: MasterEntityType;
    /** All columns currently in the table — used to list available keys. */
    columns: MasterTableColumnMeta[];
    /** An example natural key from the current data set (e.g. "SKU001"). */
    sampleEntityKey?: string;
    onUploaded: () => void;
}

const ENTITY_ID_LABEL: Record<MasterEntityType, string> = {
    PRODUCT: 'sku',
    VENDOR: 'vendor_id',
    VENDOR_PRODUCT_MAPPING: 'product_id::vendor_id',
    VENDOR_MATERIAL_MAPPING: 'material_id::vendor_id',
    VENDOR_PRICE: 'id',
    WAREHOUSE: 'warehouse_id',
    CHANNEL: 'channel_id',
    MATERIAL: 'material_id',
};

type Phase = 'idle' | 'uploading' | 'done';

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
    opened,
    onClose,
    entityType,
    columns,
    sampleEntityKey,
    onUploaded,
}) => {
    const [phase, setPhase] = useState<Phase>('idle');
    const [result, setResult] = useState<BulkUpdateResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const reset = () => {
        setPhase('idle');
        setResult(null);
        setError(null);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    // Category caps are not in the bulk CSV path; they are edited in the grid only.
    const editableColumns = columns.filter(
        (c) => c.editable && c.type !== 'CATEGORY_CAPACITY',
    );
    const idLabel = ENTITY_ID_LABEL[entityType];
    const exampleKey = sampleEntityKey ?? `${idLabel.toUpperCase()}_001`;

    // Build a compact sample CSV string
    const sampleHeaders = [idLabel, ...editableColumns.slice(0, 3).map((c) => c.key)];
    const sampleRow = [exampleKey, ...editableColumns.slice(0, 3).map(() => 'value')];
    const sampleCsv = `${sampleHeaders.join(',')}\n${sampleRow.join(',')}`;

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (inputRef.current) inputRef.current.value = '';
        setError(null);
        setPhase('uploading');
        try {
            const res = await bulkUpdateMasterCsv(entityType, file);
            setResult(res);
            setPhase('done');
            onUploaded();
        } catch {
            setError('Upload failed. Check the file format and try again.');
            setPhase('idle');
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={`Bulk update ${entityType.toLowerCase()} records`}
            size="md"
            centered
        >
            <Stack gap="md">
                {phase !== 'done' && (
                    <>
                        <Text size="sm" c="dimmed">
                            Upload a CSV to update multiple records at once. The first column must be
                            the <strong>{idLabel}</strong>. Subsequent columns can be any of the
                            available column keys below — only non-empty cells are updated.
                        </Text>

                        {/* Sample CSV */}
                        <Stack gap={4}>
                            <Text size="xs" fw={600} tt="uppercase" c="dimmed">CSV format</Text>
                            <pre
                                style={{
                                    margin: 0,
                                    padding: '8px 12px',
                                    fontSize: 11,
                                    fontFamily: 'monospace',
                                    background: 'var(--mantine-color-gray-0)',
                                    border: '1px solid var(--mantine-color-gray-3)',
                                    borderRadius: 4,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                }}
                            >
                                {sampleCsv}
                            </pre>
                        </Stack>

                        {/* Available column keys */}
                        {editableColumns.length > 0 && (
                            <Stack gap={4}>
                                <Text size="xs" fw={600} tt="uppercase" c="dimmed">Available columns</Text>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {editableColumns.map((col) => (
                                        <span
                                            key={col.key}
                                            style={{
                                                padding: '2px 7px',
                                                fontSize: 11,
                                                fontFamily: 'monospace',
                                                background: 'var(--mantine-color-blue-0)',
                                                color: 'var(--mantine-color-blue-7)',
                                                border: '1px solid var(--mantine-color-blue-2)',
                                                borderRadius: 3,
                                            }}
                                        >
                                            {col.key}
                                        </span>
                                    ))}
                                </div>
                            </Stack>
                        )}

                        {error && (
                            <Alert color="red" icon={<Warning size={16} />}>{error}</Alert>
                        )}

                        <input
                            ref={inputRef}
                            type="file"
                            accept=".csv"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                    </>
                )}

                {phase === 'idle' && (
                    <Group justify="flex-end">
                        <Button variant="subtle" color="gray" onClick={handleClose}>Cancel</Button>
                        <Button
                            leftSection={<FileCsv size={16} />}
                            onClick={() => inputRef.current?.click()}
                        >
                            Choose CSV file
                        </Button>
                    </Group>
                )}

                {phase === 'uploading' && (
                    <Stack gap="xs">
                        <Text size="sm">Uploading…</Text>
                        <Progress animated value={100} />
                    </Stack>
                )}

                {phase === 'done' && result && (
                    <Stack gap="md">
                        <Group gap="sm" align="flex-start">
                            <CheckCircle size={32} color="var(--mantine-color-green-6)" weight="fill" />
                            <Stack gap={2}>
                                <Text size="sm" fw={500}>Upload complete</Text>
                                <Text size="xs" c="dimmed">
                                    {result.updated} record{result.updated !== 1 ? 's' : ''} updated
                                    {result.skipped > 0 && `, ${result.skipped} skipped`}
                                </Text>
                            </Stack>
                        </Group>
                        {result.errors.length > 0 && (
                            <Alert color="yellow" icon={<Warning size={16} />} title="Some rows had errors">
                                <Stack gap={2}>
                                    {result.errors.slice(0, 5).map((e, i) => (
                                        <Text key={i} size="xs">{e}</Text>
                                    ))}
                                    {result.errors.length > 5 && (
                                        <Text size="xs" c="dimmed">…and {result.errors.length - 5} more</Text>
                                    )}
                                </Stack>
                            </Alert>
                        )}
                        <Group justify="flex-end">
                            <Button variant="subtle" color="gray" onClick={reset}>Upload another</Button>
                            <Button onClick={handleClose}>Done</Button>
                        </Group>
                    </Stack>
                )}
            </Stack>
        </Modal>
    );
};

export default BulkUploadModal;
